import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, concatMap, map, mergeMap, switchMap, tap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';
import { assessorAssignmentsActions } from '../actions/assessor-assignments.actions';
import { AssessorAssignmentsValueDto } from '../models';
import { HttpService } from '../services/http.service';

@Injectable()
export class AssessorAssignmentsEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getAssignments$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessorAssignmentsActions.getAssignments),
            switchMap(({ userId }) => {
                return this.httpService.getAssignments(userId).pipe(
                    finalizeLoader('assessor-assignments-get'),
                    map((payload) => assessorAssignmentsActions.getAssignmentsSuccess({ payload })),
                    catchError((response: HttpErrorResponse) => of(assessorAssignmentsActions.getAssignmentsError({ code: response.status }))),
                );
            }),
        );
    });

    getAssignmentLookups$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessorAssignmentsActions.getAssignmentsSuccess),
            mergeMap(({ payload: { organisationIds, schoolIds } }) => [
                assessorAssignmentsActions.getSchools({ organisationIds }),
                assessorAssignmentsActions.getCourses({ schoolIds }),
                assessorAssignmentsActions.getPackages({ schoolIds }),
            ]),
        );
    });

    updateAssignments$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessorAssignmentsActions.updateAssignments),
            concatMap(({ userId, value }) => {
                return this.httpService.updateAssignments(userId, new AssessorAssignmentsValueDto(value)).pipe(
                    finalizeLoader('assessor-assignments-update'),
                    map((payload) => assessorAssignmentsActions.updateAssignmentsSuccess({ payload })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.assessorAssignments.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.assessorAssignments.updateError');
                        return of(assessorAssignmentsActions.updateAssignmentsError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    getSchools$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessorAssignmentsActions.getSchools),
            switchMap(({ organisationIds }) => {
                return this.httpService.lookupSchools(organisationIds).pipe(
                    finalizeLoader('assessor-assignments-schools'),
                    map((payload) => assessorAssignmentsActions.getSchoolsSuccess({ payload })),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.assessorAssignments.schoolsError');
                        return of(assessorAssignmentsActions.getSchoolsError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    getCourses$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessorAssignmentsActions.getCourses),
            switchMap(({ schoolIds }) => {
                if (schoolIds.length === 0) {
                    return of(assessorAssignmentsActions.getCoursesSuccess({ payload: [] }));
                }

                return this.httpService.lookupCourses(schoolIds).pipe(
                    finalizeLoader('assessor-assignments-courses'),
                    map((payload) => assessorAssignmentsActions.getCoursesSuccess({ payload })),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.assessorAssignments.coursesError');
                        return of(assessorAssignmentsActions.getCoursesError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    getPackages$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessorAssignmentsActions.getPackages),
            switchMap(({ schoolIds }) => {
                if (schoolIds.length === 0) {
                    return of(assessorAssignmentsActions.getPackagesSuccess({ payload: [] }));
                }

                return this.httpService.lookupPackages(schoolIds).pipe(
                    finalizeLoader('assessor-assignments-packages'),
                    map((payload) => assessorAssignmentsActions.getPackagesSuccess({ payload })),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.assessorAssignments.packagesError');
                        return of(assessorAssignmentsActions.getPackagesError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
